/* eslint-disable no-continue */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { HiPlus } from 'react-icons/hi';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { format, parseISO, startOfDay } from 'date-fns';
import { AxiosResponse } from 'axios';

import getValidationErros from '~/utils/getValidationsErrors';
import api from '~/services/api';

import { Container } from './styles';
import Input from '~/components/Input';
import InputDate from '~/components/InputDate';
import Toast from '~/utils/toast';
import { KrTrash } from '~/components/KoroIcons';
import { IInitialData as IBasicData } from '../BasicData';
import Loading from '~/components/Loading';
import { IBatchCategory } from '../Interfaces';
import { IModalityData } from '../Modalities';

export interface IBatch {
  input_id: number;
  qtdPlaces?: number;
  expiration: Date;
  restPlaces?: number;
  batchesCategories?: IBatchCategory[];
  expired?: boolean;
  initialized?: boolean;
  qtdSold?: number;
}

interface IBatches {
  basicData: IBasicData;
  batches: IBatch[];
  onChange(
    data: IBatch[],
    basicData: IBasicData,
    batchesCategories: IBatchCategory[]
  ): void;
  batchesCategories: IBatchCategory[];
  modalities: IModalityData[];
  progressNumber?: number;
}

export interface IBatchesHandle {
  submit: () => void;
}

const Batches: ForwardRefRenderFunction<IBatchesHandle, IBatches> = (
  {
    basicData,
    onChange,
    batches: initialBatches,
    batchesCategories,
    modalities,
    progressNumber,
  },
  ref
) => {
  const formRef = useRef<FormHandles>(null);
  const [batches, setBatches] = useState<IBatch[]>([
    {
      input_id: new Date().getTime(),
      expiration: new Date(),
    },
  ]);
  const [batchesError, setBatchesError] = useState('');
  const [oldBatches, setOldBatches] = useState<IBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Carregando...');

  useEffect(() => {
    if (progressNumber && progressNumber === 7) {
      api
        .delete(`batches-categories/events/${basicData.event_id}`)
        .catch((err) => {
          console.log(err);
        });
    }
  }, [basicData.event_id, progressNumber]);

  useEffect(() => {
    if (initialBatches && initialBatches.length > 0) {
      setBatches(initialBatches);
      setOldBatches(JSON.parse(JSON.stringify(initialBatches)));
    }
  }, [initialBatches]);

  const handleAddBatch = useCallback(() => {
    setBatches((oldState) => [
      ...oldState,
      {
        input_id: new Date().getTime(),
        expiration: new Date(),
      },
    ]);
  }, []);

  const handleRemoveBatch = useCallback((input_id) => {
    Swal.fire({
      icon: 'warning',
      iconColor: '#ff6900',
      title: 'Gostaria de excluir o lote?',
      showCancelButton: true,
      cancelButtonText: 'Não',
      confirmButtonText: 'Sim',
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#B34A00',
      reverseButtons: true,
    }).then((e) => {
      if (e.isConfirmed) {
        Toast.fire({
          icon: 'success',
          iconColor: '#ff6900',
          title: 'Lote excluído com sucesso!',
        });
        setBatches((oldState) => {
          if (oldState.length === 1) {
            return [
              {
                input_id: new Date().getTime(),
                expiration: new Date(),
              },
            ];
          }
          return oldState.filter((batch) => batch.input_id !== input_id);
        });
      }
    });
  }, []);

  const handleChangeQtdPlaces = useCallback(
    (e, batchIndex) => {
      const newBatchesData = batches.slice();
      newBatchesData[batchIndex].qtdPlaces = e.target.value;
      newBatchesData[batchIndex].restPlaces = e.target.value;
      setBatches(newBatchesData);
    },
    [batches]
  );

  const handleChangeExpiration = useCallback(
    (value, batchIndex) => {
      const newBatchesData = batches.slice();
      newBatchesData[batchIndex].expiration = value;
      setBatches(newBatchesData);
    },
    [batches]
  );

  const handleSubmit = useCallback(
    async (data) => {
      if (loading) return;
      try {
        setLoading(true);
        setLoadingText('Salvando...');
        formRef.current?.setErrors({});
        setBatchesError('');

        const schema = Yup.object().shape({
          batches: Yup.string().when(`$checkBatches`, {
            is: (checkBatches: boolean) => checkBatches,
            then: Yup.string()

              .test(
                'validDate',
                `A data de expiração não pode ser posterior a ${format(
                  new Date(basicData.end_date),
                  'dd/MM/yyyy'
                )}`,
                () => {
                  const maxDate = new Date(basicData.end_date);
                  return batches.every(
                    (batch) =>
                      startOfDay(batch.expiration) <= startOfDay(maxDate)
                  );
                }
              )
              .test(
                'validDate',
                `A data de expiração precisa ser posterior a do lote anterior`,
                () => {
                  const checkBatches = batches.map((batch, index) => {
                    if (index === 0) return true;
                    return (
                      startOfDay(batch.expiration) >
                      startOfDay(batches[index - 1].expiration)
                    );
                  });
                  return checkBatches.every((batch) => batch);
                }
              )
              .test(
                'validQuantity',
                `Valor não pode ser menor que a quantidade já vendida`,
                () => {
                  return batches.every(
                    (batch) =>
                      (batch.qtdSold &&
                        batch.qtdPlaces &&
                        batch.qtdSold <= batch.qtdPlaces) ||
                      batch.qtdPlaces === 0 ||
                      !batch.qtdPlaces ||
                      !batch.qtdSold ||
                      batch.expired
                  );
                }
              ),
            otherwise: Yup.string()

              .test(
                'validDate',
                `A data de expiração não pode ser posterior a ${format(
                  new Date(basicData.end_date),
                  'dd/MM/yyyy'
                )}`,
                () => {
                  const maxDate = new Date(basicData.end_date);
                  return batches.every(
                    (batch) =>
                      startOfDay(batch.expiration) <= startOfDay(maxDate)
                  );
                }
              )
              .test(
                'validDate',
                `A data de expiração precisa ser posterior a do lote anterior`,
                () => {
                  const checkBatches = batches.map((batch, index) => {
                    if (index === 0) return true;
                    return (
                      startOfDay(batch.expiration) >
                      startOfDay(batches[index - 1].expiration)
                    );
                  });
                  return checkBatches.every((batch) => batch);
                }
              )
              .test(
                'validQuantity',
                `Valor não pode ser menor que a quantidade já vendida`,
                () => {
                  return batches.every(
                    (batch) =>
                      (batch.qtdSold &&
                        batch.qtdPlaces &&
                        batch.qtdSold <= batch.qtdPlaces) ||
                      batch.qtdPlaces?.toString() === '0' ||
                      !batch.qtdPlaces ||
                      !batch.qtdSold ||
                      batch.expired
                  );
                }
              ),
          }),
        });

        await schema.validate(data, {
          abortEarly: false,
          context: {
            checkBatches: !batches.every(
              (batch) => batch.qtdPlaces && batch.expiration
            ),
          },
        });

        const deletedBatches = oldBatches.filter(
          (oldBatch) =>
            !batches.some((modality) => modality.input_id === oldBatch.input_id)
        );

        const newBatches = JSON.parse(JSON.stringify(batches));

        // Filtra batch-categories órfãs (de modalidades/categorias deletadas)
        const validCombinations = modalities.flatMap((modality) =>
          (modality.categories || []).map((category) => ({
            modality_id: modality.id,
            category_id: category.id,
          }))
        );

        const newBatchesCategories = (
          JSON.parse(JSON.stringify(batchesCategories)) as IBatchCategory[]
        ).filter((bc) =>
          validCombinations.some(
            (combo) =>
              combo.modality_id === bc.modality_id &&
              combo.category_id === bc.category_id
          )
        );

        if (batches.length > 0) {
          let batchIndex = 0;

          for (const batch of batches) {
            const batchFormData = {
              event_id: basicData.event_id,
              name: `Lote ${batchIndex + 1}`,
              quantity: batch.qtdPlaces || 0,
              due_date: new Date(
                new Date(batch.expiration).setHours(20, 59, 59, 0)
              ),
            };

            const isOldBatch = oldBatches.some(
              (oldBatch) => oldBatch.input_id === batch.input_id
            );

            let responseBatch: AxiosResponse;
            let responseBatchCategory: AxiosResponse;
            const newBatchCategoriesAux: IBatchCategory[] = [];

            if (isOldBatch) {
              responseBatch = await api.put(
                `batches/${batch.input_id}`,
                batchFormData
              );

              // Busca orphans (sem batch_id) de combos que este lote ainda não possui
              const orphanBatchCategories = newBatchesCategories.filter(
                (bc) =>
                  !bc.batch_id &&
                  !newBatchesCategories.some(
                    (existing) =>
                      existing.batch_id === batch.input_id &&
                      existing.modality_id === bc.modality_id &&
                      existing.category_id === bc.category_id
                  )
              );

              for (const orphan of orphanBatchCategories) {
                orphan.batch_id = batch.input_id;
                await api.put(`batches-categories/${orphan.id}`, {
                  event_id: orphan.event_id,
                  batch_id: batch.input_id,
                  modality_id: orphan.modality_id,
                  category_id: orphan.category_id,
                });
              }
            } else {
              responseBatch = await api.post('batches', batchFormData);

              const newBatchId = responseBatch.data.id;

              const uniqueCombos = Array.from(
                new Map(
                  newBatchesCategories.map((bc) => [
                    `${bc.modality_id}_${bc.category_id}`,
                    {
                      modality_id: bc.modality_id,
                      category_id: bc.category_id,
                    },
                  ])
                ).values()
              );

              for (const combo of uniqueCombos) {
                // Verifica se esse combo já está atribuído a algum lote existente no array principal
                const alreadyUsed = newBatchesCategories.some(
                  (bc) =>
                    bc.modality_id === combo.modality_id &&
                    bc.category_id === combo.category_id &&
                    !!bc.batch_id
                );

                const emptyBatchCategory = !alreadyUsed
                  ? newBatchesCategories.find(
                      (bc) =>
                        bc.modality_id === combo.modality_id &&
                        bc.category_id === combo.category_id &&
                        !bc.batch_id
                    )
                  : undefined;

                if (emptyBatchCategory) {
                  // Marca imediatamente como usado para não ser roubado pelo próximo lote
                  emptyBatchCategory.batch_id = newBatchId;

                  responseBatchCategory = await api.put(
                    `batches-categories/${emptyBatchCategory.id}`,
                    {
                      event_id: emptyBatchCategory.event_id,
                      modality_id: emptyBatchCategory.modality_id,
                      batch_id: newBatchId,
                    }
                  );

                  const batchCategoryDataIndex = newBatchesCategories.findIndex(
                    (batchCategoryData) =>
                      batchCategoryData.id === emptyBatchCategory.id
                  );

                  if (batchCategoryDataIndex >= 0) {
                    newBatchesCategories[batchCategoryDataIndex].batch_id =
                      responseBatchCategory.data.batch_id;

                    newBatchCategoriesAux.push(
                      newBatchesCategories[batchCategoryDataIndex]
                    );
                  }
                } else {
                  responseBatchCategory = await api.post('batches-categories', {
                    event_id: basicData.event_id,
                    batch_id: newBatchId,
                    modality_id: combo.modality_id,
                    category_id: combo.category_id,
                  });

                  const batchCategory = {
                    id: responseBatchCategory.data.id,
                    event_id: responseBatchCategory.data.event_id,
                    batch_id: responseBatchCategory.data.batch_id,
                    modality_id: responseBatchCategory.data.modality_id,
                    category_id: responseBatchCategory.data.category_id,
                    price: '0',
                    quantity: 0,
                    quantity_sold: 0,
                    on: true,
                    new: true,
                  };
                  newBatchesCategories.push(batchCategory);
                  newBatchCategoriesAux.push(batchCategory);
                }
              }
              newBatches[batchIndex].batchesCategories = newBatchCategoriesAux;
            }

            newBatches[batchIndex].input_id = responseBatch.data.id;
            newBatches[batchIndex].expiration = parseISO(
              newBatches[batchIndex].expiration
            );

            batchIndex += 1;
          }
        }

        if (deletedBatches.length > 0) {
          for (const deletedBatch of deletedBatches) {
            if (
              deletedBatch.batchesCategories &&
              deletedBatch.batchesCategories.length > 0
            ) {
              for (const batchCategory of deletedBatch.batchesCategories) {
                await api.delete(`batches-categories/${batchCategory.id}`);
              }
            }

            await api.delete(`batches/${deletedBatch.input_id}`);
          }
        }

        const sales_end_date = newBatches[newBatches.length - 1].expiration;

        await api.put(`events/organizer/${basicData.event_id}`, {
          sales_end_date,
        });

        try {
          await api.delete(`batches-categories/events/${basicData.event_id}`);
        } catch (error) {
          console.log(error);
        }

        setBatches(newBatches);
        setOldBatches(JSON.parse(JSON.stringify(newBatches)));
        onChange(
          newBatches,
          {
            ...basicData,
            sales_end_date,
          },
          newBatchesCategories
        );
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);
          formRef.current?.setErrors(errors);
          if (errors.batches) {
            setBatchesError(errors.batches);
          }
        } else {
          console.log(error);
          Swal.fire('Oops...', 'Erro ao salvar os lotes!');
        }
      } finally {
        setLoading(false);
        setLoadingText('Carregando...');
      }
    },
    [
      basicData,
      batches,
      batchesCategories,
      loading,
      modalities,
      oldBatches,
      onChange,
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      submit: () => {
        formRef.current?.submitForm();
      },
    }),
    []
  );

  return (
    <Container ref={formRef} onSubmit={handleSubmit} className="row">
      <div className="col-12">
        <h2 className="h4 fw-semibold text-center mt-5 mb-4">7 - Lotes</h2>
        <p className="mb-4">
          Preencha a quantidade de vagas por lote e a data de vencimento, para
          seguir para o próximo passo.
        </p>
      </div>
      <div className="col-12">
        {batches.map((batch, index) => (
          <div className="bd-lots mb-3 p-3">
            <div className="d-flex">
              <button
                type="button"
                onClick={() => handleRemoveBatch(batch.input_id)}
                className={`${
                  batches.length === 1 ||
                  batch.expired ||
                  batch.initialized ||
                  (batch.qtdSold || 0) > 0
                    ? 'opacity-0 pe-none'
                    : 'opacity-1'
                } border-0 ms-auto ps-3 bg-transparent`}
              >
                <KrTrash size={14} color="#B41717" className="svg-card" />
              </button>
            </div>
            <div
              className={`${
                batch.expired ? 'pe-none' : ''
              } d-flex justify-content-between align-items-center`}
            >
              <h3 className="w-h3 h5 mb-0 fw-semibold roboto d-flex align-items-center">
                Lote {index + 1}
              </h3>
              <div>
                <span className="mx-auto w-75 roboto h6 fw-normal mb-2 d-block">
                  Qtd. de vagas
                </span>

                <Input
                  type="number"
                  name="qtd-places"
                  value={batch.qtdPlaces}
                  onChange={(e) => handleChangeQtdPlaces(e, index)}
                  className="mx-auto w-75 input"
                  onWheel={(e: any) => e.target.blur()}
                />
              </div>
              <div className="w-auto">
                <span className=" roboto h6 fw-normal mb-2 d-block">
                  Qtd.&nbsp;vendida
                </span>{' '}
                <p className="text-center mb-2">{batch.qtdSold ?? 0}</p>
              </div>

              <div>
                <span className="roboto h6 fw-normal mb-2 d-block">
                  Vencimento
                </span>
                <InputDate
                  className="py-1"
                  name="date-lot"
                  selected={startOfDay(batch.expiration)}
                  onChange={(e) => handleChangeExpiration(e, index)}
                  colorSvg="#5D5D5D"
                />
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="d-flex mt-4 justify-content-center btn-add-lot w-100 bg-transparent align-items-center p-2"
          onClick={handleAddBatch}
        >
          Adicionar novo lote
          <HiPlus size={16} color="#ff6900" className="ms-2 " />
        </button>
      </div>
      {batchesError && (
        <span className="small text-danger error mt-2">{batchesError}</span>
      )}
      <Loading active={loading} text={loadingText} />
    </Container>
  );
};

export default forwardRef(Batches);
